import {
	LowesOrderAddOns,
	OrderAddOns,
	OrderCustomColorSample,
	PlaceANewOrderText,
	StartANewQuoteText,
	UnavailableDataPlaceholderText
} from "../constants/text";
import { SkuAndDescription } from "components/NewOrders/AddLineItem/schema.ts";

export const splitWord = (word: string, length: number): string[] => {
	if (word.length <= length) {
		return [word];
	} else {
		const result: string[] = [];
		for (let i = 0; i < word.length; i += length) {
			result.push(word.substring(i, i + length));
		}
		return result;
	}
};

/**
 * Split a PascalCase string into an array of its words.  E.g. "PascalCase" =>
 * ["Pascal", "Case"].  If spaces are present in the string they are preserved
 * wherever they are
 *
 * @param text String to split
 * @returns string[] split on UpperCase letters
 */
export const splitPascalCase = (text: string) =>
	text
		.split(/([A-Z])/)
		.slice(1)
		.map((_, index, arr) => (index % 2 === 0 ? `${arr[index]}${arr[index + 1]}` : undefined))
		.filter((text) => text !== undefined);

export const checkForAndReplaceNullEmptyWhitespace = (item: string | undefined | null | number) => {
	return (typeof item === "number" && item !== 0) || (item && item.trim() !== "")
		? item
		: UnavailableDataPlaceholderText;
};

export const toFormattedPrice = (price: number | undefined | null | string): string => {
	if (price === 0 || price === "0") return "$0.00";
	if (!price) return UnavailableDataPlaceholderText;

	const parsedPrice = Number(price);
	if (isNaN(parsedPrice)) return UnavailableDataPlaceholderText;

	return `$${(parsedPrice + 0.0001).toFixed(2)}`;
};

export const convertToTitleCase = (inputString: string | undefined | null) => {
	if (inputString === null || inputString === undefined) {
		return "";
	}

	const skipWords = new Set([
		"a",
		"an",
		"as",
		"the",
		"and",
		"but",
		"or",
		"for",
		"nor",
		"on",
		"at",
		"to",
		"by",
		"with",
		"via"
	]);

	const wordExceptions: { input: string; output: string }[] = [
		{
			input: "ADD_ON",
			output: "Add-On"
		}
	];

	const foundWordExceptionOutput = wordExceptions.find((word) => word.input === inputString)?.output;

	return (
		foundWordExceptionOutput ??
		inputString
			.replaceAll("_", " ")
			.split(" ")
			.filter((word) => word.length > 0)
			.map((word) =>
				skipWords.has(word.toLowerCase())
					? word.toLowerCase()
					: word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
			)
			.join(" ")
	);
};

export const encodeQueryStringToBase64 = (url: string): string => {
	const urlObj = new URL(url);
	const encodedSearch = btoa(urlObj.searchParams.toString());
	urlObj.search = `?${encodedSearch}`;
	return urlObj.href;
};

export const decodeBase64WhenEncoded = (str: string): string => {
	const base64regex = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/;
	const isEncoded = base64regex.test(str);
	return isEncoded ? atob(str) : str;
};

export const capitalizeFirstLetter = (str: string | null | undefined): string => {
	return str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : "";
};

export const nullSafeLocaleCompare = (a: string | null | undefined, b: string | null | undefined): number => {
	if (a === null || a === undefined) {
		return b === null || b === undefined ? 0 : -1;
	}
	if (b === null || b === undefined) {
		return 1;
	}
	return a.localeCompare(b);
};

export const formatPrice = (price: number | undefined | null): string => {
	if (price === undefined) return "$0.00";
	return new Intl.NumberFormat("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2
	}).format(price ?? 0);
};

// New Order, Custom Color, Add-On
export const getNewOrderTitle = (
	isAddOn: boolean,
	isCustomColor: boolean,
	isLowesUser: boolean,
	isNewQuote: boolean
): string => {
	if (isAddOn) {
		return isLowesUser ? LowesOrderAddOns : OrderAddOns;
	}
	if (isCustomColor) {
		return OrderCustomColorSample;
	}
	if (isNewQuote) {
		return StartANewQuoteText;
	}
	return PlaceANewOrderText;
};

export const getNewOrderURL = (
	isAddOn: boolean | undefined,
	isCustomColor: boolean | undefined,
	isNewQuote: boolean | undefined,
	pagePath: string,
	originalOrderId?: string
): string => {
	if (isAddOn) {
		return `/add-ons/${pagePath}/${originalOrderId}`;
	}
	if (isCustomColor) {
		return `/custom-color/${pagePath}`;
	}
	if (isNewQuote) {
		return `/new-quote/${pagePath}`;
	}
	return `/new-order/${pagePath}`;
};

export const formatPriceWithPrefix = (
	price: number | null | undefined,
	showPlus: boolean = false,
	acceptZero?: boolean
) => {
	if (price === null || price === undefined) return UnavailableDataPlaceholderText;
	if (price === 0 && !acceptZero) return UnavailableDataPlaceholderText;

	return `${showPlus && price > 0 ? "+" : ""}${price.toLocaleString("en-US", {
		style: "currency",
		currency: "USD"
	})}`;
};

export const sortSkuAndDescription = (items: SkuAndDescription[] | undefined): SkuAndDescription[] | undefined => {
	return items?.sort((a, b) => {
		const aKey = a.sku?.trim().toLowerCase() ?? a.description?.trim().toLowerCase() ?? "";
		const bKey = b.sku?.trim().toLowerCase() ?? b.description?.trim().toLowerCase() ?? "";
		return aKey.localeCompare(bKey);
	});
};

export const convertSkuAndDescriptionToString = (sku?: string | null, desc?: string | null) =>
	[sku, desc].filter(Boolean).join(" - ");
